import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { Layout } from './components/Layout';
import { Lock } from 'lucide-react';

// — Lazy loaded pages —
const Landing = React.lazy(() => import('./pages/Landing'));
const Discover = React.lazy(() => import('./pages/Discover'));
const Odak = React.lazy(() => import('./pages/Odak'));
const Home = React.lazy(() => import('./pages/Home'));
const QuestionBank = React.lazy(() => import('./pages/QuestionBank'));
const QuestionEditor = React.lazy(() => import('./pages/QuestionEditor'));
const Tests = React.lazy(() => import('./pages/Tests'));
const TestCreate = React.lazy(() => import('./pages/TestCreate'));
const TestView = React.lazy(() => import('./pages/TestView'));
const Books = React.lazy(() => import('./pages/Books'));
const BookView = React.lazy(() => import('./pages/BookView'));
const Solve = React.lazy(() => import('./pages/Solve'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const SolutionHistory = React.lazy(() => import('./pages/SolutionHistory'));
const QuestionAnalytics = React.lazy(() => import('./pages/QuestionAnalytics'));
const TestResultDetail = React.lazy(() => import('./pages/TestResultDetail'));
const Settings = React.lazy(() => import('./pages/Settings'));
const PdfToTest = React.lazy(() => import('./pages/PdfToTest'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const Fallback = () => (
  <div className="soru-loading min-h-[60vh]">Yükleniyor...</div>
);

// Giriş gerektiren rotalar için şık Korumalı Sayfa Bileşeni
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <div className="p-4 md:p-10 max-w-lg mx-auto min-h-[60vh] flex items-center justify-center">
          <div className="soru-card p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center space-y-4 shadow-xl">
            <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto">
              <Lock size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Giriş Yapılması Gerekiyor
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Soru bankası, soru yazma ve test oluşturma modüllerine erişmek için hesabınızla giriş yapmanız gerekmektedir.
            </p>
            <div className="pt-2">
              <SignInButton mode="modal">
                <button className="soru-btn soru-btn-primary px-8 py-2.5 text-xs w-full shadow-md">
                  Giriş Yap / Kaydol
                </button>
              </SignInButton>
            </div>
          </div>
        </div>
      </SignedOut>
    </>
  );
}

import { ErrorBoundary } from './components/ErrorBoundary';

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
        <Routes>
          {/* Public: çözüm sayfası — giriş gerekmez */}
          <Route path="/coz/:shareCode" element={<Solve />} />

          {/* Ana layout içindeki sayfalar */}
          <Route path="/" element={<Layout />}>
            {/* Kök path: herkese açık tanıtım sayfası — giriş gerekmez */}
            <Route index element={<Landing />} />

            {/* Herkese açık "Testleri Keşfet" sayfası — giriş gerekmez */}
            <Route path="kesfet" element={<Discover />} />

            {/* Odak: herkese açık, hesap gerektirmeyen bağımsız Pomodoro sayfası */}
            <Route path="odak" element={<Odak />} />

            {/* Korumalı rotalar */}
            <Route
              path="pano"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="soru-havuzu"
              element={
                <ProtectedRoute>
                  <QuestionBank />
                </ProtectedRoute>
              }
            />
            <Route
              path="soru-yaz"
              element={
                <ProtectedRoute>
                  <QuestionEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="soru-yaz/:id"
              element={
                <ProtectedRoute>
                  <QuestionEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="testler"
              element={
                <ProtectedRoute>
                  <Tests />
                </ProtectedRoute>
              }
            />
            <Route
              path="test-olustur"
              element={
                <ProtectedRoute>
                  <TestCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="test-olustur/:id"
              element={
                <ProtectedRoute>
                  <TestCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="test/:id"
              element={
                <ProtectedRoute>
                  <TestView />
                </ProtectedRoute>
              }
            />
            <Route
              path="kitaplik"
              element={
                <ProtectedRoute>
                  <Books />
                </ProtectedRoute>
              }
            />
            <Route
              path="kitaplik/:id"
              element={
                <ProtectedRoute>
                  <BookView />
                </ProtectedRoute>
              }
            />
            <Route
              path="analiz"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="analiz/:testId"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="cozum-gecmisi"
              element={
                <ProtectedRoute>
                  <SolutionHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="soru-analizleri"
              element={
                <ProtectedRoute>
                  <QuestionAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="sonuc/:resultId"
              element={
                <ProtectedRoute>
                  <TestResultDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="ayarlar"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="pdf-tara"
              element={
                <ProtectedRoute>
                  <PdfToTest />
                </ProtectedRoute>
              }
            />
            {/* Eski rotalar — yer imleri/eski linkler kırılmasın diye yeni adrese yönlendir */}
            <Route path="pdf-batch-import" element={<Navigate to="/pdf-tara" replace />} />
            <Route path="pdf-to-test" element={<Navigate to="/pdf-tara" replace />} />
          </Route>

          {/* 404 fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ErrorBoundary>
  );
}

export default App;
